// ─── Theme bootstrap (Phase 11) ─────────────────────────────────────
// Runs before React mounts so the saved theme is applied during first
// paint. Lives here (not as an inline <script> in index.html) because the
// production CSP doesn't include `'unsafe-inline'` for scripts. Module
// scripts loaded from `'self'` (this bundle) are allowed.
;(function bootstrapTheme() {
  try {
    const stored = localStorage.getItem('hjalptorget:theme')
    const prefersDark =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    const resolved =
      stored === 'light' || stored === 'dark'
        ? stored
        : prefersDark
          ? 'dark'
          : 'light'
    if (resolved === 'dark') document.documentElement.classList.add('dark')
    const meta = document.querySelector('meta[name=theme-color]')
    if (meta instanceof HTMLMetaElement) {
      meta.content = resolved === 'dark' ? '#0f1219' : '#2d6040'
    }
  } catch {
    // localStorage / matchMedia unavailable — ignore.
  }
})()

import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/lib/query-client'
import { env } from '@/env'
import { colors } from '@/design-system/tokens'
import '@/lib/i18n'
import App from '@/App'
import './index.css'

// ─── Dev-mode service status log (PROMPT-0-007) ────────────────────
// Logs which third-party services are real vs mocked on app boot.
if (env.isDev) {
  const services = [
    { name: 'Stripe', enabled: env.STRIPE_ENABLED },
    { name: 'Criipto/BankID', enabled: env.CRIIPTO_ENABLED },
    { name: 'Mapbox', enabled: env.MAPBOX_ENABLED },
    { name: 'Turnstile', enabled: env.TURNSTILE_ENABLED },
    { name: 'Resend', enabled: env.RESEND_ENABLED },
    { name: 'Twilio', enabled: env.TWILIO_ENABLED },
    { name: 'Anthropic', enabled: env.ANTHROPIC_ENABLED },
    { name: 'Bolagsverket', enabled: env.BOLAGSVERKET_ENABLED },
  ] as const

  const real = services.filter((s) => s.enabled).map((s) => s.name)
  const mocked = services.filter((s) => !s.enabled).map((s) => s.name)

  // eslint-disable-next-line no-console
  console.info(
    `%c[Hjälptorget] Services — real: [${real.join(', ') || 'none'}] | mock: [${mocked.join(', ') || 'none'}]`,
    `color: ${colors.primary[500]}; font-weight: bold`,
  )
}

// ─── Sentry init (prod only, when DSN present) ─────────────────────
if (env.isProd && env.SENTRY_ENABLED) {
  import('@/lib/sentry').then((m) => m.initSentry()).catch(() => {})
}

// ─── Service worker (PWA) — deployed domain only ───────────────────
// On localhost we never want a service worker. Workbox caches aggressively,
// which otherwise forces a DevTools "Clear site data" dance to see code
// changes. So on any local origin we actively unregister any worker left
// over from a prod build, `vite preview`, or the live site, wipe its caches,
// and do ONE guarded auto-reload to drop its control. After that a single
// refresh always shows the latest code straight from the server. The full
// PWA (offline + autoUpdate) still runs on the real deployed domain.
const swHost = typeof window !== 'undefined' ? window.location.hostname : ''
const isLocalhost =
  swHost === 'localhost' ||
  swHost.endsWith('.localhost') ||
  swHost === '127.0.0.1' ||
  swHost === '0.0.0.0' ||
  swHost === '::1' ||
  swHost === '[::1]'

if ('serviceWorker' in navigator) {
  if (isLocalhost) {
    void (async () => {
      try {
        const regs = await navigator.serviceWorker.getRegistrations()
        await Promise.all(regs.map((r) => r.unregister()))
        if ('caches' in window) {
          const keys = await caches.keys()
          await Promise.all(keys.map((k) => caches.delete(k)))
        }
        // Reload once (guarded so we never loop) if a worker was still
        // controlling this page, so assets come fresh from the dev server.
        const RELOAD_FLAG = 'hjalptorget:sw-cleanup-reloaded'
        if (
          navigator.serviceWorker.controller &&
          !sessionStorage.getItem(RELOAD_FLAG)
        ) {
          sessionStorage.setItem(RELOAD_FLAG, '1')
          window.location.reload()
        }
      } catch {
        // best-effort cleanup — ignore
      }
    })()
  } else if (env.isProd) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch(() => {
        // SW registration failed — non-critical
      })
    })
  }
}

const root = document.getElementById('root')
if (!root) throw new Error('Root element not found')

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
)
